import { useCallback } from "react";
// src/components/superAdmin/WorkflowEditor.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkflowAssignment, WorkflowConfig, WorkflowDefinition, OrganizationRole } from '../../../../interfaces/superAdmin/SuperAdmin';
import { deleteAssignment, getAssignments, getDefinitions, upsertAssignment, upsertDefinition, getWorkflowReferenceData } from '../../../../services/superAdminServices/SuperAdminWorkflowService';
import { getRoles } from '../../../../services/superAdminServices/SuperAdminPolicyService'; 
import { useAuth } from '../../../../firebase/AuthProvider';
import { ArrowLeft, Edit2, Trash2, Plus, X } from 'lucide-react';
import Select from 'react-select';
import { ApprovalRoleOption, approvalRoleOptionsForStep, selectedApprovalRole, replaceApprovalRole, workflowSelectStyles } from '../../../ui/Select/approvalRoleOptions';
import '../../formFillComponents/DynamicFormComponents/FormInput.css';
import './WorkflowEditor.css';

const EMPTY_CONFIG: WorkflowConfig = {
    requireTwoApprovers: false, allowSelfApproval: false, steps: []
};

const WorkflowEditor: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { orgId } = useParams<{ orgId: string }>();
    const numericOrgId = parseInt(orgId || "0");

    const [activeTab, setActiveTab] = useState<'templates' | 'rules'>('templates');
    const [loading, setLoading] = useState(false);

    // Data State
    const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
    const [assignments, setAssignments] = useState<WorkflowAssignment[]>([]);
    
    // Reference Data
    const [regions, setRegions] = useState<{ id: number, name: string }[]>([]);
    const [dynamicRoles, setDynamicRoles] = useState<OrganizationRole[]>([]); // New V2 Roles
    const [recurringTasks, setRecurringTasks] = useState<{ id: number, name: string }[]>([]);
    const [interimTasks, setInterimTasks] = useState<{ id: number, name: string }[]>([]);

    const [editingDef, setEditingDef] = useState<Partial<WorkflowDefinition> | null>(null);
    const roleOptions = dynamicRoles.map(role => ({ value: String(role.id), label: role.name }));
    const [editingAssignment, setEditingAssignment] = useState<Partial<WorkflowAssignment> | null>(null);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        
        const [defs, assigns, workflowRefs, fetchedRoles] = await Promise.all([
            getDefinitions(authToken, numericOrgId),
            getAssignments(authToken, numericOrgId),
            getWorkflowReferenceData(authToken, numericOrgId),
            getRoles(authToken, numericOrgId) // Fetch Dynamic Roles
        ]);

        if (defs) setDefinitions(defs);
        if (assigns) setAssignments(assigns);
        if (fetchedRoles) setDynamicRoles(fetchedRoles);

        if (workflowRefs) {
            setRegions(workflowRefs.regions || []);
            setRecurringTasks(workflowRefs.recurringTemplates?.map((t: any) => ({ id: t.id, name: t.title || t.name })) || []);
            setInterimTasks(workflowRefs.interimTemplates?.map((t: any) => ({ id: t.id, name: t.title || t.name })) || []);
        }
        setLoading(false);
    }, [currentUser, numericOrgId]);

useEffect(() => {
        loadAllData();
    }, [numericOrgId, loadAllData]);



    const handleSaveDef = async () => {
        if (!editingDef || !editingDef.name) return;
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        const result = await upsertDefinition(authToken, numericOrgId, {
            id: editingDef.id,
            name: editingDef.name,
            workflowConfig: editingDef.workflowConfig || EMPTY_CONFIG
        });
        if (result) {
            setEditingDef(null);
            loadAllData();
        }
    };

    const updateStepRole = (index: number, option: ApprovalRoleOption | null) => {
        if (!editingDef?.workflowConfig) return;
        const newSteps = editingDef.workflowConfig.steps.map((step, current) =>
            current === index ? replaceApprovalRole(step, option) : step);
        setEditingDef({ ...editingDef, workflowConfig: { ...editingDef.workflowConfig, steps: newSteps } });
    };

    const addStep = () => {
        if (!editingDef?.workflowConfig) return;
        const defaultRole = "";
        const newSteps = [...(editingDef.workflowConfig.steps || []), { order: (editingDef.workflowConfig.steps?.length || 0) + 1, rolePolicy: defaultRole }];
        setEditingDef({ ...editingDef, workflowConfig: { ...editingDef.workflowConfig, steps: newSteps } });
    };

    const removeStep = (index: number) => {
        if (!editingDef?.workflowConfig) return;
        const newSteps = (editingDef.workflowConfig.steps || []).filter((_, i) => i !== index);
        newSteps.forEach((s, i) => s.order = i + 1);
        setEditingDef({ ...editingDef, workflowConfig: { ...editingDef.workflowConfig, steps: newSteps } });
    };

    const handleSaveAssignment = async () => {
        if (!editingAssignment || !editingAssignment.workflowDefinitionId) return;
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        
        const result = await upsertAssignment(authToken, numericOrgId, {
            id: editingAssignment.id,
            workflowDefinitionId: editingAssignment.workflowDefinitionId,
            priority: editingAssignment.priority || 0,
            regionIds: editingAssignment.regionIds?.length ? editingAssignment.regionIds : undefined,
            templateTaskIds: editingAssignment.templateTaskIds?.length ? editingAssignment.templateTaskIds : undefined,
            templateInterimTaskIds: editingAssignment.templateInterimTaskIds?.length ? editingAssignment.templateInterimTaskIds : undefined,
        });

        if (result) {
            setEditingAssignment(null);
            loadAllData();
        }
    };

    const handleDeleteAssignment = async (id: number) => {
        if (!window.confirm("Delete this rule?")) return;
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        await deleteAssignment(authToken, numericOrgId, id);
        loadAllData();
    };

    if (loading) return <div className="super-admin-workflow-editor p-8 text-center text-gray-500 font-medium h-screen flex items-center justify-center">Loading Configuration...</div>;

    return (
        <div className="super-admin-workflow-editor w-full h-screen flex flex-col p-4 bg-gray-50 overflow-hidden">
            {/* Header & Back Button */}
            <div className="mb-4 flex items-center justify-between flex-shrink-0">
                <button 
                    onClick={() => navigate(-1)} 
                    className="flex items-center text-[#7B5CD6] hover:text-[#5e45a3] font-medium text-sm transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Dashboard
                </button>
                <h1 className="text-xl font-bold text-gray-800 mb-0">Workflow Manager</h1>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4 flex-shrink-0">
                <button
                    className={`px-6 py-3 font-bold text-sm transition-colors duration-200 relative ${
                        activeTab === 'templates' 
                        ? 'text-[#7B5CD6]' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('templates')}
                >
                    1. Workflow Templates
                    {activeTab === 'templates' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#7B5CD6]"></span>}
                </button>
                <button
                    className={`px-6 py-3 font-bold text-sm transition-colors duration-200 relative ${
                        activeTab === 'rules' 
                        ? 'text-[#7B5CD6]' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('rules')}
                >
                    2. Assignment Rules
                    {activeTab === 'rules' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#7B5CD6]"></span>}
                </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-h-0 flex flex-col">
                {/* TAB 1: TEMPLATES */}
                {activeTab === 'templates' && (
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 min-h-0">
                        {/* Sidebar List */}
                        <div className="col-span-1 md:col-span-4 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                            <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center flex-shrink-0">
                                <h3 className="font-semibold text-gray-800 text-sm tracking-wide mb-0">Templates</h3>
                                <button 
                                    onClick={() => setEditingDef({ name: "New Workflow", workflowConfig: EMPTY_CONFIG })} 
                                    className="flex items-center text-xs bg-[#7B5CD6] text-white px-3 py-1.5 rounded hover:bg-[#6a4bc0] transition-colors shadow-sm"
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" /> New
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-white">
                                {definitions.map(def => (
                                    <div 
                                        key={def.id} 
                                        onClick={() => setEditingDef(def)} 
                                        className={`p-3 rounded-md cursor-pointer transition-all border-l-4 ${editingDef?.id === def.id ? 'bg-purple-50 border-[#7B5CD6]' : 'border-transparent hover:bg-gray-50'}`}
                                    >
                                        <div className="font-semibold text-gray-900 text-sm">{def.name}</div>
                                        <div className="text-xs text-gray-500 mt-1">{def.workflowConfig?.steps?.length || 0} Steps • {def.isActive ? 'Active' : 'Inactive'}</div>
                                    </div>
                                ))}
                                {definitions.length === 0 && (
                                    <div className="p-8 text-center text-gray-400 text-sm">No templates found.</div>
                                )}
                            </div>
                        </div>

                        {/* Editor Area */}
                        <div className="col-span-1 md:col-span-8 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                            {editingDef ? (
                                <div className="flex-1 overflow-y-auto p-6">
                                    <div className="mb-6 pb-2 border-b border-gray-100">
                                        <h3 className="font-bold text-lg text-gray-800 mb-0">
                                            {editingDef.id ? 'Edit Template' : 'Create New Template'}
                                        </h3>
                                    </div>
                                    
                                    <div className="mb-6">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Template Name</label>
                                        <input 
                                            className="w-full bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-gray-900 transition-all shadow-sm" 
                                            value={editingDef.name} 
                                            onChange={(e) => setEditingDef({ ...editingDef, name: e.target.value })} 
                                            placeholder="e.g., Standard Review Process"
                                        />
                                    </div>

                                    <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox"
                                                className="form-v2-checkbox w-4 h-4 text-[#7B5CD6] rounded border-gray-300 focus:ring-[#7B5CD6] transition-colors"
                                                checked={editingDef.workflowConfig?.requireTwoApprovers}
                                                onChange={(e) => setEditingDef({
                                                    ...editingDef,
                                                    workflowConfig: { ...editingDef.workflowConfig!, requireTwoApprovers: e.target.checked }
                                                })}
                                            />
                                            <span className="text-sm font-bold text-gray-700">Require Two Unique Approvers</span>
                                        </label>
                                    </div>

                                    <div className="space-y-3 mb-8">
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Approval Steps</label>
                                        {editingDef.workflowConfig?.steps?.map((step, idx) => {
                                            return (
                                                <div key={idx} className="flex gap-3 items-center bg-white p-3 rounded border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                                                    <span className="flex items-center justify-center bg-gray-100 text-gray-600 font-bold w-8 h-8 rounded-full text-sm shrink-0">{idx + 1}</span>
                                                    <div className="flex-1">
                                                        <Select
                                                            aria-label={`Approval role for step ${idx + 1}`}
                                                            className="form-v2-react-select react-select-container" classNamePrefix="react-select"
                                                            value={selectedApprovalRole(step, roleOptions)}
                                                            options={approvalRoleOptionsForStep(roleOptions, editingDef.workflowConfig?.steps || [], idx)}
                                                            onChange={option => updateStepRole(idx, option)}
                                                            placeholder="Select approval role..." menuPortalTarget={document.body}
                                                            menuPosition="fixed" menuPlacement="auto" maxMenuHeight={280} styles={workflowSelectStyles}
                                                        />
                                                    </div>
                                                    <button onClick={() => removeStep(idx)} className="text-gray-300 hover:text-red-500 p-1 transition-colors">
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <button 
                                            onClick={addStep} 
                                            className="w-full py-3 border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 rounded hover:border-[#7B5CD6] hover:text-[#7B5CD6] hover:bg-purple-50 transition-all font-bold text-sm flex justify-center items-center"
                                        >
                                            <Plus className="w-4 h-4 mr-2" /> Add Approval Step
                                        </button>
                                    </div>

                                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                        <button 
                                            onClick={() => setEditingDef(null)} 
                                            className="px-4 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded text-sm font-bold transition-colors shadow-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={handleSaveDef} 
                                            className="px-6 py-2 bg-[#7B5CD6] text-white rounded text-sm font-bold hover:bg-[#6a4bc0] shadow-sm transition-colors"
                                        >
                                            Save Template
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-gray-400 p-12">
                                    <p className="font-medium">Select a template from the left to edit, or create a new one.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* TAB 2: RULES */}
                {activeTab === 'rules' && (
                    <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white flex-shrink-0">
                            <h3 className="font-bold text-gray-800 mb-0">Active Rules</h3>
                            <button 
                                onClick={() => setEditingAssignment({ priority: 10, regionIds: [] })} 
                                className="flex items-center bg-[#7B5CD6] text-white px-4 py-2 rounded hover:bg-[#6a4bc0] font-bold text-sm shadow-sm transition-colors"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add New Rule
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <table className="w-full text-left border-separate border-spacing-0">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Priority</th>
                                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Workflow Template</th>
                                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Region Scope</th>
                                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Task Scope</th>
                                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {assignments.map(rule => (
                                        <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4">
                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 border border-gray-200 text-gray-700 font-bold text-xs">
                                                    {rule.priority}
                                                </span>
                                            </td>
                                            <td className="p-4 font-semibold text-gray-900">
                                                {definitions.find(d => d.id === rule.workflowDefinitionId)?.name || rule.workflowDefinitionId}
                                            </td>
                                            <td className="p-4 text-sm text-gray-600">
                                                {rule.regionIds?.length ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {rule.regionIds.map(rid => (
                                                            <span key={rid} className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium border border-gray-200">
                                                                {regions.find(r => r.id === rid)?.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-gray-400 italic text-xs">All Regions</span>}
                                            </td>
                                            <td className="p-4 text-sm">
                                                {rule.templateTaskIds?.length ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded border border-blue-200 text-xs font-bold bg-blue-50 text-blue-700">
                                                        Recurring ({rule.templateTaskIds.length})
                                                    </span>
                                                ) : rule.templateInterimTaskIds?.length ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded border border-orange-200 text-xs font-bold bg-orange-50 text-orange-700">
                                                        Interim ({rule.templateInterimTaskIds.length})
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 italic text-xs">Global Default</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                <button onClick={() => setEditingAssignment(rule)} className="text-[#7B5CD6] hover:text-[#5e45a3] mr-4 transition-colors">
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDeleteAssignment(rule.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {assignments.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="p-8 text-center text-gray-400 text-sm font-medium">No rules defined for this organization.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* EDIT MODAL */}
                        {editingAssignment && (
                            <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                                <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto flex flex-col">
                                    <div className="mb-6 pb-2 border-b border-gray-100 flex-shrink-0">
                                        <h3 className="font-bold text-xl text-gray-800 mb-0">Edit Assignment Rule</h3>
                                    </div>
                                    <div className="space-y-5 flex-1 overflow-y-auto pr-1">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Workflow Template</label>
                                            <select 
                                                className="w-full border border-gray-300 bg-white p-2 rounded focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] shadow-sm text-sm" 
                                                value={editingAssignment.workflowDefinitionId || ""} 
                                                onChange={(e) => setEditingAssignment({ ...editingAssignment, workflowDefinitionId: parseInt(e.target.value) })}
                                            >
                                                <option value="">Select Template...</option>
                                                {definitions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                        </div>
                                        
                                        {/* SCOPE SELECTION */}
                                        <div className="bg-gray-50 p-4 rounded border border-gray-200">
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-3">Task Application Scope</label>
                                            <div className="flex flex-col gap-2 mb-4">
                                                <label className="flex items-center gap-3 cursor-pointer p-2 rounded border border-transparent hover:border-gray-200 hover:bg-white transition-colors">
                                                    <input type="radio" name="taskScope" className="w-4 h-4 text-[#7B5CD6] focus:ring-[#7B5CD6] border-gray-300"
                                                        checked={!editingAssignment.templateTaskIds?.length && !editingAssignment.templateInterimTaskIds?.length}
                                                        onChange={() => setEditingAssignment({ ...editingAssignment, templateTaskIds: [], templateInterimTaskIds: [] })} />
                                                    <div>
                                                        <span className="block text-sm font-bold text-gray-900">Global (Any Task)</span>
                                                        <span className="block text-xs text-gray-500">Applies to all tasks unless overridden</span>
                                                    </div>
                                                </label>
                                                <label className="flex items-center gap-3 cursor-pointer p-2 rounded border border-transparent hover:border-gray-200 hover:bg-white transition-colors">
                                                    <input type="radio" name="taskScope" className="w-4 h-4 text-[#7B5CD6] focus:ring-[#7B5CD6] border-gray-300"
                                                        checked={!!editingAssignment.templateTaskIds?.length}
                                                        onChange={() => setEditingAssignment({ ...editingAssignment, templateTaskIds: [recurringTasks[0]?.id], templateInterimTaskIds: [] })} />
                                                    <div>
                                                        <span className="block text-sm font-bold text-gray-900">Specific Recurring Templates</span>
                                                        <span className="block text-xs text-gray-500">Only applies to specific recurring tasks</span>
                                                    </div>
                                                </label>
                                                <label className="flex items-center gap-3 cursor-pointer p-2 rounded border border-transparent hover:border-gray-200 hover:bg-white transition-colors">
                                                    <input type="radio" name="taskScope" className="w-4 h-4 text-[#7B5CD6] focus:ring-[#7B5CD6] border-gray-300"
                                                        checked={!!editingAssignment.templateInterimTaskIds?.length}
                                                        onChange={() => setEditingAssignment({ ...editingAssignment, templateTaskIds: [], templateInterimTaskIds: [interimTasks[0]?.id] })} />
                                                    <div>
                                                        <span className="block text-sm font-bold text-gray-900">Specific Interim Templates</span>
                                                        <span className="block text-xs text-gray-500">Only applies to specific ad-hoc templates</span>
                                                    </div>
                                                </label>
                                            </div>

                                            {/* Multi-Select: Recurring */}
                                            {editingAssignment.templateTaskIds && (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Hold Ctrl/Cmd to select multiple templates</label>
                                                    <select multiple className="w-full border border-blue-200 p-2 rounded h-32 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 text-sm shadow-sm"
                                                        value={editingAssignment.templateTaskIds.map(String)}
                                                        onChange={(e) => setEditingAssignment({ ...editingAssignment, templateTaskIds: Array.from(e.target.selectedOptions, o => parseInt(o.value)) })}>
                                                        {recurringTasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                    </select>
                                                </div>
                                            )}

                                            {/* Multi-Select: Interim */}
                                            {editingAssignment.templateInterimTaskIds && (
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 mb-1">Hold Ctrl/Cmd to select multiple templates</label>
                                                    <select multiple className="w-full border border-orange-200 p-2 rounded h-32 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 text-sm shadow-sm"
                                                        value={editingAssignment.templateInterimTaskIds.map(String)}
                                                        onChange={(e) => setEditingAssignment({ ...editingAssignment, templateInterimTaskIds: Array.from(e.target.selectedOptions, o => parseInt(o.value)) })}>
                                                        {interimTasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                                    </select>
                                                </div>
                                            )}
                                        </div>

                                        {/* REGION SELECTION */}
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Region Restrictions (Optional)</label>
                                            <select multiple className="w-full border border-gray-300 bg-white p-2 rounded h-24 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-sm shadow-sm"
                                                value={editingAssignment.regionIds?.map(String) || []}
                                                onChange={(e) => setEditingAssignment({ ...editingAssignment, regionIds: Array.from(e.target.selectedOptions, o => parseInt(o.value)) })}>
                                                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                            </select>
                                            <p className="text-xs text-gray-400 mt-1 font-medium">Leave empty to apply to all regions.</p>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Priority (Higher Wins)</label>
                                            <input type="number" 
                                                className="w-full border border-gray-300 bg-white p-2 rounded focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-sm shadow-sm" 
                                                value={editingAssignment.priority} 
                                                onChange={(e) => setEditingAssignment({ ...editingAssignment, priority: parseInt(e.target.value) })} 
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100 flex-shrink-0">
                                        <button 
                                            onClick={() => setEditingAssignment(null)} 
                                            className="px-4 py-2 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded text-sm font-bold transition-colors shadow-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={handleSaveAssignment} 
                                            className="px-6 py-2 bg-[#7B5CD6] text-white rounded text-sm font-bold hover:bg-[#6a4bc0] shadow-sm transition-colors"
                                        >
                                            Save Rule
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WorkflowEditor;